<script setup lang="ts">
import { ref, watch } from 'vue'
import { Loader2 } from 'lucide-vue-next'

const props = defineProps<{
  data: Record<string, any>
  isSaving: boolean
}>()

const emits = defineEmits(['update', 'next'])

const localData = ref({ ...props.data })
const errors = ref<Record<string, string>>({})

watch(localData, (val) => emits('update', val), { deep: true })

const validateAndProceed = () => {
  const newErrors: Record<string, string> = {}

  if (!localData.value.companyName.trim()) {
    newErrors.companyName = 'Company name is required'
  }

  if (!localData.value.companyEmail.trim()) {
    newErrors.companyEmail = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localData.value.companyEmail)) {
    newErrors.companyEmail = 'Please enter a valid email address'
  }

  errors.value = newErrors

  if (Object.keys(newErrors).length === 0) {
    emits('next')
  }
}
</script>

<template>
  <div class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm mt-8">
    <!-- Header -->
    <div class="grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6">
      <div class="leading-none font-semibold text-lg">Get Started</div>
      <div class="text-muted-foreground text-sm">
        Let's begin with some basic information
      </div>
    </div>

    <!-- Content -->
    <div class="px-6">
      <div class="grid gap-6">
        <!-- Company Name -->
        <div class="flex flex-col gap-1">
          <label class="font-medium text-sm">Company Name</label>
          <input
            v-model="localData.companyName"
            type="text"
            placeholder="Acme Mining Corp"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand outline-none"
            :aria-invalid="!!errors.companyName"
          />
          <p v-if="errors.companyName" class="text-destructive text-xs mt-1">{{ errors.companyName }}</p>
        </div>

        <!-- Company Email -->
        <div class="flex flex-col gap-1">
          <label class="font-medium text-sm">Company Email</label>
          <input
            v-model="localData.companyEmail"
            type="email"
            placeholder="contact@acmemining.com"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand outline-none"
            :aria-invalid="!!errors.companyEmail"
          />
          <p v-if="errors.companyEmail" class="text-destructive text-xs mt-1">{{ errors.companyEmail }}</p>
        </div>

        <!-- Info box -->
        <div class="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
          We'll use this to create your company record so you can continue your registration later if needed.
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-end px-6">
      <button
        class="px-4 py-2 rounded-md bg-brand text-white text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-70 disabled:cursor-not-allowed"
        @click="validateAndProceed"
        :disabled="props.isSaving"
      >
        <template v-if="props.isSaving">
          <Loader2 class="w-4 h-4 animate-spin" />
          <span>Saving...</span>
        </template>
        <template v-else>
          <span>Next</span>
        </template>
      </button>
    </div>
  </div>
</template>